import React from 'react';
import { hapticFeedback } from '../utils/haptic';

interface MobileTestInterfaceProps {
  frequency: number;
  isPlaying: boolean;
  isWaitingForResponse: boolean;
  onPlay: () => void;
  onStop: () => void;
  onAnswer: (answer: string) => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
  hasStarted: boolean;
}

export const MobileTestInterface: React.FC<MobileTestInterfaceProps> = ({
  frequency,
  isPlaying,
  isWaitingForResponse,
  onPlay,
  onStop,
  onAnswer,
  volume,
  onVolumeChange,
  hasStarted,
}) => {
  const handleVolumeAdjust = (delta: number) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    if (newVolume !== volume) {
      hapticFeedback.sliderChange();
      onVolumeChange(newVolume);
    }
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col md:hidden">
      {/* Header */}
      <header className="h-12 px-4 flex items-center justify-between bg-white border-b border-purple-100">
        <h1 className="text-lg font-semibold text-purple-700">TinnitusTune</h1>
        <div className="text-sm text-gray-600">{hasStarted ? 'Step 2 of 4' : 'Assessment'}</div>
      </header>
      
      <div className="flex-1 flex flex-col px-6 py-4 overflow-y-auto">
        {!hasStarted ? (
          /* Start screen */
          <div className="flex-1 flex flex-col justify-center items-center text-center">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-purple-700 mb-4">Ready to Find Your Frequency?</h2>
              <p className="text-gray-600 mb-6">This quick test will help identify your tinnitus frequency</p>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-6 mb-8 max-w-sm w-full">
              <h3 className="font-semibold text-purple-700 mb-3">Before we start:</h3>
              <ul className="text-left space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span>Put on headphones</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span>Find a quiet space</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span>Takes about 5 minutes</span>
                </li>
              </ul>
            </div>
            
            <button
              onClick={onPlay}
              className="w-full max-w-sm py-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-2xl text-xl font-bold shadow-lg transform active:scale-95 transition-all duration-200"
            >
              Start Test
            </button>
            
            <p className="text-sm text-gray-500 mt-4">No registration required</p>
          </div>
        ) : (
          <>
        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 w-1/2 transition-all duration-300" />
          </div>
        </div>
        
        {/* Frequency display */}
        <div className="text-center mb-8">
          <div className="text-sm text-gray-600 mb-2">Currently Testing</div>
          <div className="text-4xl font-bold text-purple-700">{frequency.toLocaleString()} Hz</div>
        </div>
        
        {/* Visual frequency representation */}
        <div className="flex-1 flex items-center justify-center mb-8">
          <div className="flex items-end justify-center gap-1 h-32 w-full max-w-xs">
            {[...Array(15)].map((_, i) => {
              const isCenter = i === 7;
              const height = isCenter ? 20 : 50 + Math.sin(i * 0.5) * 30;
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-t transition-all duration-300 ${
                    isCenter ? 'bg-pink-500' : 'bg-purple-400'
                  } ${isPlaying ? 'animate-pulse' : ''}`}
                  style={{ 
                    height: `${height}%`,
                    animationDelay: `${i * 50}ms`
                  }}
                />
              );
            })}
          </div>
        </div>
        
        {/* Volume control with visual bars */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Volume</span>
            <span className="text-sm font-medium text-purple-700">{Math.round(volume * 100)}%</span>
          </div>
          <div className="flex gap-1 h-8 mb-3">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`flex-1 rounded transition-all duration-200 ${
                  i < Math.round(volume * 10) 
                    ? 'bg-gradient-to-t from-purple-500 to-purple-400' 
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleVolumeAdjust(-0.1)}
              className="py-3 bg-purple-100 rounded-xl text-2xl font-bold text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
              aria-label="Decrease volume"
            >
              −
            </button>
            <button
              onClick={() => handleVolumeAdjust(0.1)}
              className="py-3 bg-purple-100 rounded-xl text-2xl font-bold text-purple-700 tap-highlight-none active:bg-purple-200 transition-colors"
              aria-label="Increase volume"
            >
              +
            </button>
          </div>
        </div>
        
        {/* Main play/stop button */}
        <button
          onClick={() => {
            if (isPlaying) {
              hapticFeedback.audioStop();
              onStop();
            } else {
              hapticFeedback.audioStart();
              onPlay();
            }
          }}
          className={`w-full py-6 rounded-2xl text-xl font-bold mb-6 tap-highlight-none transition-all duration-200 transform active:scale-95 ${
            isPlaying 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg'
          }`}
        >
          {isPlaying ? '⏸ Stop Sound' : '▶ Play Sound'}
        </button>
        
        {/* Answer buttons */}
        {isWaitingForResponse && (
          <div className="space-y-3">
            <p className="text-center text-gray-700 mb-3 font-medium">
              How does this compare to your tinnitus?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  hapticFeedback.answerQuestion();
                  onAnswer('lower');
                }}
                className="py-5 bg-purple-100 hover:bg-purple-200 rounded-xl font-semibold text-purple-700 tap-highlight-none transition-colors transform active:scale-95"
              >
                Lower
              </button>
              <button
                onClick={() => {
                  hapticFeedback.answerQuestion();
                  onAnswer('higher');
                }}
                className="py-5 bg-purple-100 hover:bg-purple-200 rounded-xl font-semibold text-purple-700 tap-highlight-none transition-colors transform active:scale-95"
              >
                Higher
              </button>
            </div>
            <button
              onClick={() => {
                hapticFeedback.answerQuestion();
                onAnswer('match');
              }}
              className="w-full py-5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold tap-highlight-none transition-all duration-200 transform active:scale-95 shadow-lg"
            >
              It's a Match!
            </button>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};